import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { LogOut, LayoutDashboard, Menu, X } from 'lucide-react';
import { useState, useEffect } from 'react';

const Navbar = ({ transparent = false }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Check if we're on the homepage
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      // Transition after scrolling past 80vh (video header height)
      const scrollThreshold = window.innerHeight * 0.8;
      setScrolled(window.scrollY > scrollThreshold);
    };

    if (transparent && isHomePage) {
      window.addEventListener('scroll', handleScroll);
      handleScroll(); // Check initial state
      return () => window.removeEventListener('scroll', handleScroll);
    } else {
      setScrolled(true); // Always solid on other pages
    }
  }, [transparent, isHomePage]);

  const handleLogout = () => {
    logout();
    navigate('/');
    setMobileMenuOpen(false);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // Determine navbar style based on scroll state - teal with opacity
  const navbarBg = (transparent && isHomePage && !scrolled)
    ? 'bg-[#38B2AC]/20 backdrop-blur-sm'
    : 'bg-[#38B2AC]/90 backdrop-blur-md border-b border-white/10';

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${navbarBg}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-3 md:py-4">
        <div className="flex items-center justify-between">
          {/* Logo - responsive sizing */}
          <Link to="/" className="flex items-center gap-2 sm:gap-3 flex-shrink-0" data-testid="logo-link" onClick={closeMobileMenu}>
            <img
              src="https://customer-assets.emergentagent.com/job_d01d4c74-fff7-4a42-b6e0-751e81917d22/artifacts/y2v3dbbh_logo.png"
              alt="Toasty Media"
              className="h-8 sm:h-10 md:h-12 w-auto"
            />
            <span className="text-lg sm:text-2xl md:text-3xl lg:text-4xl font-black text-white tracking-wide whitespace-nowrap" style={{
              fontFamily: '"Impact", "Haettenschweiler", "Arial Narrow Bold", sans-serif',
              letterSpacing: '0.04em',
              textShadow: '1px 1px 0px #DD7638, 2px 2px 0px rgba(221, 118, 56, 0.7), 3px 3px 6px rgba(0, 0, 0, 0.4)'
            }}>TOASTY MEDIA</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  className="flex items-center gap-2 text-white/90 hover:text-white transition-colors font-semibold"
                  data-testid="dashboard-link"
                >
                  <LayoutDashboard size={20} />
                  <span>Dashboard</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 text-white/90 hover:text-white transition-colors font-semibold"
                  data-testid="logout-button"
                >
                  <LogOut size={20} />
                  <span>Logout</span>
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/video-production"
                  className="text-white/90 hover:text-white transition-colors font-bold tracking-wide text-sm xl:text-base"
                  data-testid="video-production-link"
                >
                  Video Production
                </Link>
                <Link
                  to="/services"
                  className="text-white/90 hover:text-white transition-colors font-bold tracking-wide text-sm xl:text-base"
                  data-testid="services-link"
                >
                  Systems
                </Link>
                <Link
                  to="/case-studies"
                  className="text-white/90 hover:text-white transition-colors font-bold tracking-wide text-sm xl:text-base"
                  data-testid="case-studies-link"
                >
                  The Playbook
                </Link>
                <Link
                  to="/about"
                  className="text-white/90 hover:text-white transition-colors font-bold tracking-wide text-sm xl:text-base"
                  data-testid="about-link"
                >
                  Philosophy
                </Link>
                <Link
                  to="/login"
                  className="text-white/90 hover:text-white transition-colors font-bold tracking-wide text-sm xl:text-base"
                  data-testid="login-link"
                >
                  Login
                </Link>
                <Link
                  to="/lead-form"
                  className="px-4 xl:px-6 py-2 xl:py-3 bg-[#DD7638] hover:bg-[#F6AD55] text-white font-bold uppercase tracking-wider transition-all text-sm xl:text-base"
                  data-testid="start-call-link"
                >
                  Schedule a Call
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden text-white p-2 -mr-2"
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden border-t border-white/20 bg-[#1D4E4D]/95 backdrop-blur-xl"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 space-y-2">
              {user ? (
                <>
                  <Link
                    to="/dashboard"
                    className="flex items-center gap-3 text-white/90 hover:text-white hover:bg-white/10 transition-colors font-bold text-lg py-3 px-3 rounded-lg"
                    data-testid="mobile-dashboard-link"
                    onClick={closeMobileMenu}
                  >
                    <LayoutDashboard size={24} />
                    <span>Dashboard</span>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 text-white/90 hover:text-white hover:bg-white/10 transition-colors font-bold text-lg py-3 px-3 rounded-lg w-full text-left"
                    data-testid="mobile-logout-button"
                  >
                    <LogOut size={24} />
                    <span>Logout</span>
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/video-production"
                    className="block text-white/90 hover:text-white hover:bg-white/10 transition-colors font-bold text-lg py-3 px-3 rounded-lg"
                    data-testid="mobile-video-production-link"
                    onClick={closeMobileMenu}
                  >
                    Video Production
                  </Link>
                  <Link
                    to="/services"
                    className="block text-white/90 hover:text-white hover:bg-white/10 transition-colors font-bold text-lg py-3 px-3 rounded-lg"
                    data-testid="mobile-services-link"
                    onClick={closeMobileMenu}
                  >
                    Systems
                  </Link>
                  <Link
                    to="/case-studies"
                    className="block text-white/90 hover:text-white hover:bg-white/10 transition-colors font-bold text-lg py-3 px-3 rounded-lg"
                    data-testid="mobile-case-studies-link"
                    onClick={closeMobileMenu}
                  >
                    The Playbook
                  </Link>
                  <Link
                    to="/about"
                    className="block text-white/90 hover:text-white hover:bg-white/10 transition-colors font-bold text-lg py-3 px-3 rounded-lg"
                    data-testid="mobile-about-link"
                    onClick={closeMobileMenu}
                  >
                    Philosophy
                  </Link>
                  <Link
                    to="/login"
                    className="block text-white/90 hover:text-white hover:bg-white/10 transition-colors font-bold text-lg py-3 px-3 rounded-lg"
                    data-testid="mobile-login-link"
                    onClick={closeMobileMenu}
                  >
                    Login
                  </Link>
                  <Link
                    to="/lead-form"
                    className="block w-full text-center px-6 py-4 bg-[#DD7638] hover:bg-[#F6AD55] text-white font-bold uppercase tracking-wider transition-all mt-4 rounded-lg"
                    data-testid="mobile-start-call-link"
                    onClick={closeMobileMenu}
                  >
                    Schedule a Call
                  </Link>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
